import { Fragment, forwardRef, Ref } from 'react';
import { Listbox } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@v2/shared/lib/utils';

export type Option = {
  value: any;
  label: string;
  disabled?: boolean;
};

type SelectProps = {
  className?: string;
  value?: Option;
  onChange?: (option: Option) => void;
  options: Option[];
  name?: string;
  disabled?: boolean;
  defaultValue?: Option;
};

/**
 * A dropdown that takes and returns whole options rather than their values.
 *
 * Off headless_jshop. Built on a Listbox rather than a `<select>` because the option list
 * has to be stylable, and `by="value"` because the value is the option object — two
 * renders produce two objects for the same choice, and identity comparison would leave
 * nothing selected.
 *
 * The option shape is what react-hook-form's Controller stores, so `{...field}` spreads
 * straight onto this and a form reads `watch('name').value`.
 */
function Select(
  { className, value, onChange, options, name, disabled, defaultValue }: SelectProps,
  ref: Ref<HTMLDivElement>
) {
  return (
    <Listbox
      name={name}
      by="value"
      value={value}
      onChange={onChange}
      disabled={disabled}
      defaultValue={defaultValue}
    >
      <div className={cn('relative', className)} ref={ref}>
        <Listbox.Button className="group relative flex justify-between w-full border-[1.5px] border-solid border-slate-300 hover:border-slate-400 bg-white focus:border-slate-900 py-1.5 text-gray-900 px-2.5 rounded text-left font-medium z-0 disabled:opacity-50 disabled:cursor-not-allowed">
          {value?.label}
          <FontAwesomeIcon icon={faAngleDown} className="text-slate-400 p-1 group-focus:text-slate-900" />
        </Listbox.Button>

        <Listbox.Options className="border-slate-200 border-[1.5px] border-solid rounded absolute mt-1 w-full drop-shadow-md bg-white overflow-hidden z-10 list-none p-0 m-0">
          {options?.map((option) => (
            <Listbox.Option key={option.label} value={option} disabled={option.disabled} as={Fragment}>
              {({ active, selected }) => (
                <li
                  className={cn('m-1 p-1 rounded cursor-pointer font-medium', {
                    'bg-slate-50': active && !selected,
                    'bg-red-50 text-red-600 font-semibold': selected,
                    'text-gray-800': !active && !selected,
                  })}
                >
                  <FontAwesomeIcon icon={faCheck} fixedWidth className={cn('mr-1', !selected && 'opacity-0')} />
                  {option?.label}
                </li>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </div>
    </Listbox>
  );
}

export default forwardRef(Select);
